import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS, IMAGES, LOGO_URL } from '@/data/site';

const VALUES = [
  { title: 'Quality first', text: 'A curated menu where every product earns its place. If it is not premium, it does not make the shelf.' },
  { title: 'Customer service', text: 'Real people answer the phone. Every order is verified by a human who knows the menu and the city.' },
  { title: 'Convenience', text: 'Online ordering built to be fast — from first click to checkout in minutes, seven days a week.' },
];

export default function About() {
  return (
    <>
      <SEO
        title="About | Majestic Greens DMV — Premium Cannabis in Washington, DC"
        description={`Majestic Greens DMV has served Washington, DC since ${BUSINESS.founded} with premium cannabis, genuine customer service, and easy online ordering. Located at ${BUSINESS.address}.`}
        path="/about"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: 'About Majestic Greens DMV',
          url: 'https://majesticgreensdmv.com/about',
          about: { '@id': 'https://majesticgreensdmv.com/#business' },
          isPartOf: { '@id': 'https://majesticgreensdmv.com/#website' },
        }}
      />
      <div data-testid="about-page" className="pb-28 pt-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid items-center gap-16 lg:grid-cols-2">
            <Reveal>
              <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-bronze">Our Story</p>
              <h1 className="mt-4 font-serif text-4xl text-ink sm:text-6xl">Rooted in the District since {BUSINESS.founded}</h1>
              <p className="mt-6 text-sm leading-relaxed text-sage">
                Majestic Greens DMV began with a simple idea: Washington, DC deserves a cannabis retailer that treats quality and service as non-negotiable. From our home at {BUSINESS.address} in downtown DC, we have grown into a trusted neighborhood destination for premium flower, vapes, edibles, and concentrates.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-sage">
                What has never changed is how we work. A carefully curated menu. A team that actually picks up the phone. An ordering experience designed around your time, not ours. That is the standard we set in {BUSINESS.founded} — and the standard we keep today.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="grain relative overflow-hidden border border-ink/10">
                <img src={IMAGES.dc} alt="Washington DC at sunset" className="aspect-[4/3] w-full object-cover" />
                <div className="absolute inset-0 bg-black/40" />
                <img src={LOGO_URL} alt="Majestic Greens DMV logo" className="absolute bottom-6 left-6 h-20 w-20 rounded-full border-2 border-gold/50" />
              </div>
            </Reveal>
          </div>

          <div className="mt-28 grid gap-10 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="border-t border-gold/40 pt-8">
                  <span className="font-serif text-5xl text-forest">0{i + 1}</span>
                  <h2 className="mt-4 font-serif text-2xl text-ink">{v.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-sage">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-24 flex flex-col items-center border border-ink/10 bg-surface p-12 text-center">
              <h2 className="font-serif text-3xl text-ink sm:text-4xl">Experience it yourself</h2>
              <p className="mt-4 max-w-lg text-sm text-sage">
                Browse the live menu and see why DC keeps coming back. ${BUSINESS.minOrder} minimum order, adults 21+.
              </p>
              <Link
                to="/shop"
                data-testid="about-shop-cta"
                className="group mt-8 inline-flex items-center gap-3 bg-gold px-10 py-5 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bronzehover"
              >
                Shop the Menu <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </Reveal>
        </div>
      </div>
    </>
  );
}
